import {
  BulbOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  MailOutlined,
  StopOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import { Button, Popconfirm, Tag, Tooltip } from "antd";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePlayers,
  editTeamPlayers,
} from "../../../store/actions/team.actions";
import { DeletePopConfirmContent } from "./PlayerTable";

export const SelectedItemsControl = ({
  selected,
  validation,
  clearSelection,
  handleSendWelcomeEmail,
  canEditPlayer,
  canDeletePlayer,
  canSendWelcomeEmail,
}) => {
  const dispatch = useDispatch();

  const disabled = useMemo(() => !selected.length, [selected]);

  const selectedIds = useMemo(
    () => selected.map(({ player_id }) => player_id),
    [selected]
  );

  const {
    info: {
      allowed_active_player_count,
      allowed_player_count
    },
    allPlayers
  } = useSelector(({ team }) => team);

  const isAllInvalid = selected.every(({ valid }) => !valid);

  const tooltipTitle = disabled ? "Select players first" : undefined;

  const handleDeleteSelected = async () => {
    await dispatch(deletePlayers(selectedIds));
    clearSelection();
  };

  const handleActivateSelected = async () => {
    await dispatch(
      editTeamPlayers(
        selected
          .filter(({ valid }) => valid)
          .map(({ player_id, first_name, last_name, number }) => ({
            retired: false,
            player_id,
            first_name,
            last_name,
            number
          }))
      )
    );
  };

  const handleInactivateSelected = async () => {
    await dispatch(
      editTeamPlayers(
        selected.map(({ player_id }) => ({ retired: true, player_id }))
      )
    );
  };

  const handleSendEmailToSelected = async () => {
    const filtered = selected.filter(({ email }) => email);
    await Promise.all(
      filtered.map((player) => handleSendWelcomeEmail(player)())
    );
  };

  return (
    <div
      style={{
        marginBottom: 16,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <div
        style={{
          paddingRight: 16,
          display: "flex",
          gap: 16,
          alignItems: "center",
        }}
      >
        {!!selected.length && (
          <div style={{ fontSize: 15, fontWeight: "bold" }}>
            {selected.length} players selected
          </div>
        )}
        <Popconfirm
          placement="left"
          title={DeletePopConfirmContent}
          okText="Yes"
          cancelText="No"
          onConfirm={handleDeleteSelected}
          disabled={disabled || !canDeletePlayer}
        >
          <Tooltip title={!canDeletePlayer ? "" : (disabled ? "Select players first" : undefined)}>
            <Button
              danger
              type="primary"
              disabled={disabled || !canDeletePlayer}
              style={{ display: "flex", alignItems: "center" }}
            >
              Delete
            </Button>
          </Tooltip>
        </Popconfirm>
        <Tooltip
          title={
            tooltipTitle ||
            (isAllInvalid &&
              "Please fix the player details before setting them to active")
          }
        >
          <Button
            variant="outlined"
            disabled={disabled || isAllInvalid || !canEditPlayer}
            onClick={handleActivateSelected}
            style={{
              display: "flex",
              alignItems: "center",
              ...((disabled || isAllInvalid || !canEditPlayer)
                ? {}
                : { color: "#389e0d", borderColor: "#389e0d" }),
            }}
          >
            Set to active
          </Button>
        </Tooltip>
        <Tooltip
          title={
            tooltipTitle ||
            (isAllInvalid &&
              "Please fix the player details before setting them to retired")
          }
        >
          <Button
            danger
            disabled={disabled || isAllInvalid || !canEditPlayer}
            onClick={handleInactivateSelected}
            style={{ display: "flex", alignItems: "center" }}
          >
            Set to retired
          </Button>
        </Tooltip>
        <Tooltip
          title={
            !canSendWelcomeEmail ? "" : (
              tooltipTitle ||
              (
                isAllInvalid &&
                "Please fix the player details before sending welcome emails"
              )
            )
          }
        >
          <Button
            color="primary"
            variant="outlined"
            disabled={disabled || isAllInvalid || !canSendWelcomeEmail}
            onClick={handleSendEmailToSelected}
            style={{ display: "flex", alignItems: "center" }}
          >
            Send welcome email
          </Button>
        </Tooltip>
      </div>
      <div style={{ display: "flex" }}>
        <Tooltip title={
          <div>
            <p style={{marginBottom: '0px'}}>Roster Capacity</p>
            <p style={{marginBottom: '0px'}}>Total Players: {allPlayers?.length}/{allowed_player_count}</p>
            <p style={{marginBottom: '0px'}}>Active Players: {allPlayers?.filter((e) => e.retired === false).length}/{allowed_active_player_count}</p>
          </div>
          }>
          <Tag
            icon={<CheckCircleOutlined />}
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 15,
              padding: "4px 8px",
            }}
            color="green"
          >
            {validation?.valid || 0}
          </Tag>
        </Tooltip>
        <Tooltip title="Invalid players">
          <Tag
            icon={<CloseCircleOutlined style={{marginRight: '8px'}} />}
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 15,
              padding: "4px 8px",
            }}
            color="error"
          >
            {validation?.error || 0}
          </Tag>
        </Tooltip>
      </div>
    </div>
  );
};
